<script setup lang="ts">
/**
 * LandingAxes — 랜딩(WS-00) 축 소개 섹션(id="axes").
 *
 * 축을 균등 그리드로 나열하지 않고 **깔때기**로 그린다 — 랜딩의 주장이 "한 번 입력하면 분석과 리포트가
 * 이어진다"이므로 배치 자체가 그 연결을 보여야 한다(내 입시카드가 여러 기능 중 하나로 읽히면 실패).
 *
 *   1  내 입시카드      전폭 (source)   ← 모든 분석이 쓰는 재료
 *      ↓ 커넥터
 *   2  성적 분석 (· 진로심리검사 — 노출 보류)  병렬 (analysis)  ← 재료를 소비
 *   —  리포트 보관함    하단 바 (archive) ← 결과 적재(위계 한 단 낮춤)
 *
 * 행 구성은 `axesByFlow()` 가 데이터에서 고른다 — 컴포넌트에 축 key 하드코딩 금지.
 * 카드 1장의 마크업은 LandingAxisCard, 하단 바는 LandingArchiveBar 가 소유한다.
 * 이동·게이트 분기는 페이지가 단일 소유 — go 를 그대로 올린다.
 */
import { axesByFlow, type LandingGoTarget } from '#/data/analysis/landingAxes';

import LandingArchiveBar from './LandingArchiveBar.vue';
import LandingAxisCard from './LandingAxisCard.vue';

defineEmits<{
  /** open 축 카드 클릭 — 페이지가 게이트 판정 후 축 대표 화면으로 이동. */
  go: [target: LandingGoTarget];
}>();

const sourceAxes = axesByFlow('source');
const analysisAxes = axesByFlow('analysis');
const archiveAxes = axesByFlow('archive');
</script>

<template>
  <section id="axes" class="wrap ws-section">
    <div class="ws-section-head">
      <div>
        <p class="ws-eyebrow">이렇게 쓰면 됩니다</p>
        <h2 class="ws-section-title">성적 한 번만 넣으면 끝까지 이어져요</h2>
        <p class="ws-section-desc">
          내 성적을 입시카드에 한 번 저장해 두세요. 정시·수시 분석이 그 성적을 그대로 사용하고,
          결과는 리포트 보관함에 그대로 남습니다.
        </p>
      </div>
    </div>

    <div class="ws-flow">
      <!-- 1단 — 재료(전폭) -->
      <div class="ws-row">
        <LandingAxisCard
          v-for="axis in sourceAxes"
          :key="axis.key"
          :axis="axis"
          @go="$emit('go', $event)"
        />
      </div>

      <p class="ws-link" aria-hidden="true">
        <span class="ws-link-label">저장한 성적을 그대로 가져와요</span>
      </p>

      <!-- 2단 — 분석(축이 둘 이상이면 2열 병렬, 하나면 전폭) -->
      <div :class="['ws-row', { 'ws-row--split': analysisAxes.length > 1 }]">
        <LandingAxisCard
          v-for="axis in analysisAxes"
          :key="axis.key"
          :axis="axis"
          @go="$emit('go', $event)"
        />
      </div>

      <!-- 적재 — 카드보다 낮은 위계의 바 -->
      <div class="ws-archive">
        <LandingArchiveBar
          v-for="axis in archiveAxes"
          :key="axis.key"
          :axis="axis"
          @go="$emit('go', $event)"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ── 컨테이너(.wrap) — LandingHero 와 동일 복제(full-bleed 섹션 관행). ── */
.wrap {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 18px;
}

@media (width >= 600px) {
  .wrap {
    padding: 0 32px;
  }
}

@media (width >= 1080px) {
  .wrap {
    padding: 0 40px;
  }
}

/* ── 섹션 헤드 ── */
.ws-section {
  /*
   * 세로만 지정(padding-block) — 좌우 gutter 는 같은 요소의 .wrap 이 소유(shorthand 로 덮으면 소실).
   * 랜딩 마지막 섹션이라 하단 여백은 히어로와 같은 리듬(60/84)으로 잡아 푸터와 붙지 않게 한다.
   */
  padding-block: 56px 60px;
}

@media (width >= 980px) {
  .ws-section {
    padding-block: 78px 84px;
  }
}

.ws-section-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 30px;
}

.ws-eyebrow {
  margin: 0 0 14px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--accent-deep);
}

.ws-section-title {
  margin: 0;
  font-size: 25px;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: var(--brand-ink);
  line-height: 1.34;
}

@media (width >= 980px) {
  .ws-section-title {
    font-size: 30px;
  }
}

.ws-section-desc {
  margin: 12px 0 0;
  max-width: 60ch;
  font-size: 14.5px;
  line-height: 1.75;
  color: var(--body);
}

/* ── 깔때기 ── */
.ws-flow {
  display: flex;
  flex-direction: column;
}

.ws-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

/*
 * 2단 분석 행 — ≥720 부터 나란히(2열). 그 아래는 세로로 쌓인다.
 * 불릿 개수가 축마다 달라(3 vs 4) 카드 높이가 어긋나므로 `stretch` 로 맞춘다 — 병렬 두 축은
 * 같은 단계라 높이까지 같아야 "나란히 선택지"로 읽힌다.
 */
@media (width >= 720px) {
  .ws-row--split {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: stretch;
  }
}

/*
 * 단 사이 커넥터 — 세로선 + 가운데 라벨. 흐름을 "읽히게" 하는 장치라 장식(aria-hidden)이지만,
 * 라벨 문구가 연결의 의미를 말하므로 선만 두지 않는다.
 */
.ws-link {
  position: relative;
  display: flex;
  justify-content: center;
  margin: 0;
  padding-block: 14px;
}

.ws-link::before {
  content: '';
  position: absolute;
  inset-block: 0;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: linear-gradient(
    to bottom,
    var(--line) 0%,
    var(--line) 40%,
    transparent 40%,
    transparent 60%,
    var(--line) 60%
  );
}

.ws-link-label {
  position: relative;
  z-index: 1;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--muted);
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: 999px;
}

@media (width >= 980px) {
  .ws-link {
    padding-block: 18px;
  }
}

/* 적재 바 — 수렴 카드와 살짝 띄우되 커넥터보다 가깝게(같은 단의 부속이라는 인상) */
.ws-archive {
  margin-top: 16px;
}
</style>
